import { Relation } from '../../api/fetchPatientDirByDocOid';
import { GeniePadElementsSettingItem } from '../../api/pushGenie';
import { Age } from '../types/patient';
import { Followup, PadElements, Tool } from '../types/prescription';
import { Visit } from '../types/visit';
import { generatePatientId, generatePrescriptionId, generateVisitId } from '../utils';
import moment from 'moment';

export type VisitActions = CreateVisitAction;

export const CREATE_VISIT = 'CREATE_VISIT';
export const DOWNLOAD_PT_DIR = 'DOWNLOAD_PT_DIR';

export interface NewPatient {
    id?: string;
    uuid?: string;
    localId?: string;
    name: string;
    phCode?: string;
    phoneNumber?: string;
    age?: Age;
    gender: string;
    bloodgroup?: string;
    onApp?: boolean;
    'health-ids'?: string[];
    formData?: {
        label: string;
        type: 'number' | 'string' | 'select';
        key: string;
        value: string | number;
    }[];
    abha?: {
        id?: string;
        records?: number;
    };
    relations?: Relation[];
}

export interface CreateVisitAction {
    type: typeof CREATE_VISIT;
    visit: Visit;
    // to set up surroundings
    meta: {
        patient: NewPatient;
        pxData?: Tool;
        abortVisitId?: string;
        autoCopy: boolean;
        avoidTriggerBEStartVisit?: boolean;
    };
    onSuccess?: (prescriptionId: string) => void;
    onFailure?: () => void;
}

export const initVisit = (
    patient: NewPatient,
    docid: string,
    clinicId: string,
    visitId?: string,
    onSuccess?: (prescriptionId: string) => void,
    px?: { date: string; tool: Tool },
    visitMeta?: {
        visitType?: string;
        saId?: string;
        abortVisitId?: string;
        autoCopy: boolean;
        avoidTriggerBEStartVisit?: boolean;
        padElements?: PadElements[];
    },
    onFailure?: () => void,
): CreateVisitAction | undefined => {
    const patientLocalId =
        patient.localId ||
        generatePatientId(patient.phoneNumber || (Math.random() * 10000).toString(), patient.name);

    const visitPatient = {
        id: patient.id,
        localId: patientLocalId,
        uuid: patient.uuid,
        phone: {
            c: patient.phCode || '+91',
            n: patient.phoneNumber,
        },
        name: {
            f: patient.name,
        },
        formData: patient.formData,
        'health-ids': patient['health-ids'],
        abha: patient.abha,
        relations: patient['relations'],
    };

    if (!docid) {
        console.error('docId not found');
        return;
    }

    let followup: Followup | undefined;

    if (px && px.tool.followup?.date) {
        const today = moment(new Date());
        const pxDate = moment(px.date);
        const pxFollowUpDate = moment(px?.tool.followup?.date);

        followup = {
            date: today.add(pxFollowUpDate.diff(pxDate, 'days'), 'days').toISOString(),
            notes: px.tool.followup?.notes,
        };
    }

    const vid = visitId || generateVisitId(docid);

    return {
        type: CREATE_VISIT,
        meta: {
            patient,
            pxData: px?.tool && {
                ...px?.tool,
                followup: followup,
            },
            abortVisitId: visitMeta?.abortVisitId,
            autoCopy: visitMeta?.autoCopy || false,
            avoidTriggerBEStartVisit: visitMeta?.avoidTriggerBEStartVisit || false,
        },
        visit: {
            id: vid,
            docid: docid,
            patient: visitPatient,
            prescriptionId: generatePrescriptionId(docid, vid),
            createdAt: new Date(),
            startedAt: new Date(),
            isCompleted: 0,
            clinicId,
            visitType: visitMeta?.visitType,
            saId: visitMeta?.saId,
        },
        onSuccess,
        onFailure,
    };
};
